import type { CSSProperties } from 'react';

import { useInView } from '../lib/useInView';

const steps = [
  {
    num: 'STEP 01',
    title: 'Measure',
    body: 'A short problem. You explain. You attempt. We see how far you get before reaching for help.',
    time: '~2 min',
  },
  {
    num: 'STEP 02',
    title: 'Practice',
    body: 'A few minutes a day. Hints are available; ready-made answers are not. After each session you see exactly what you missed.',
    time: '5–15 min/day',
  },
  {
    num: 'STEP 03',
    title: 'Get independent',
    body: 'A short plan aimed at your weak spots. You revisit similar problems later — with less help each time.',
    time: 'Ongoing',
  },
] as const;

/** Stagger delay per step — bar + card animate together, left → right. */
const STAGGER_MS = 180;

export const HowItWorks = () => {
  const { ref, inView } = useInView<HTMLElement>({ threshold: 0.22, once: true });

  return (
    <section
      ref={ref}
      className={`section how${inView ? ' how--revealed' : ''}`}
      id="how"
      aria-labelledby="how-title"
    >
      <div className="container">
        <p className="eyebrow">How it works</p>
        <h2 id="how-title" className="h-section">
          Three steps. No answers handed to you — just progress.
        </h2>
        <p className="lede">
          From sign-up to your first practice session in minutes. No tutorial maze. No &ldquo;I
          finished the course and still freeze&rdquo; feeling.
        </p>

        <div
          className="how__bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={steps.length}
          aria-valuenow={inView ? steps.length : 0}
          aria-label="Onboarding progress"
        >
          {steps.map((s, i) => (
            <span
              key={s.num}
              className="how__bar-cell"
              style={{ '--step-i': i } as CSSProperties}
              aria-hidden="true"
            />
          ))}
        </div>

        <ol className="how__steps how__steps--3">
          {steps.map((s, i) => (
            <li
              key={s.num}
              className="step"
              style={{ '--step-i': i, '--stagger': `${i * STAGGER_MS}ms` } as CSSProperties}
            >
              <span className="step__num">{s.num}</span>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
              <span className="step__time">{s.time}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
